import CryptoJS from 'crypto-js'
import { findAgiliteUser, updateAgiliteUser } from './utils'

/**
 * Generates a secure hash from a passphrase using PBKDF2
 * @param {string} passphrase - The user's passphrase
 * @param {string} salt - Optional salt (user's email or phone will be used if not provided)
 * @returns {string} - The hashed passphrase
 */
export const hashPassphrase = (passphrase, salt) => {
  if (!passphrase || !salt) {
    throw new Error('Passphrase and salt are required')
  }

  // Use PBKDF2 with 10000 iterations for security
  const hash = CryptoJS.PBKDF2(passphrase, salt, {
    keySize: 256 / 32, // 256-bit key
    iterations: 10000
  })

  return hash.toString(CryptoJS.enc.Hex)
}

/**
 * Verifies a passphrase against a stored hash
 * @param {string} passphrase - The user's entered passphrase
 * @param {string} storedHash - The stored hash from the database
 * @param {string} salt - The salt used for hashing
 * @returns {boolean} - Whether the passphrase is valid
 */
export const verifyPassphrase = (passphrase, storedHash, salt) => {
  try {
    const hashedInput = hashPassphrase(passphrase, salt)
    return hashedInput === storedHash
  } catch (error) {
    console.error('Error verifying passphrase:', error)
    return false
  }
}

/**
 * Sets up a new passphrase for a user
 * @param {string} userId - The user's Agilite ID
 * @param {string} passphrase - The new passphrase
 * @param {string} salt - The salt to use (typically email or phone)
 * @returns {Promise<Object>} - Updated user object
 */
export const setupUserPassphrase = async (userId, passphrase, salt) => {
  try {
    if (!passphrase || passphrase.length < 8) {
      throw new Error('Passphrase must be at least 8 characters long')
    }

    const hashedPassphrase = hashPassphrase(passphrase, salt)

    const updatedUser = await updateAgiliteUser(userId, {
      'extraData.backupPassphrase': {
        hash: hashedPassphrase,
        salt: salt,
        createdAt: new Date().toISOString(),
        lastUsed: null
      }
    })

    return updatedUser
  } catch (error) {
    console.error('Error setting up passphrase:', error)
    throw error
  }
}

/**
 * Authenticates a user using their backup passphrase
 * @param {string} identifier - Email or phone number
 * @param {string} passphrase - The user's passphrase
 * @returns {Promise<Object|null>} - User object if authentication successful, null otherwise
 */
export const authenticateWithPassphrase = async (identifier, passphrase) => {
  try {
    // Find user by email or phone - try both separately since findAgiliteUser handles OR logic internally
    let user = null

    // Try to find by email first
    if (identifier.includes('@')) {
      user = await findAgiliteUser({ email: identifier })
    } else {
      // Try to find by phone number
      user = await findAgiliteUser({ phoneNumber: identifier })
    }

    if (!user) {
      throw new Error('User not found')
    }

    // Check if user has a backup passphrase set up
    if (!user.extraData?.backupPassphrase?.hash) {
      throw new Error('No backup passphrase configured for this user')
    }

    const { hash: storedHash, salt } = user.extraData.backupPassphrase

    // Verify the passphrase
    const isValid = verifyPassphrase(passphrase, storedHash, salt)

    if (!isValid) {
      throw new Error('Invalid passphrase')
    }

    // Update last used timestamp
    await updateAgiliteUser(user._id, {
      'extraData.backupPassphrase.lastUsed': new Date().toISOString()
    })

    return user
  } catch (error) {
    console.error('Error authenticating with passphrase:', error)
    throw error
  }
}

/**
 * Checks if a user has a backup passphrase configured
 * @param {Object} user - The user object
 * @returns {boolean} - Whether the user has a passphrase set up
 */
export const userHasPassphrase = (user) => {
  return !!user?.extraData?.backupPassphrase?.hash
}

/**
 * Validates passphrase strength
 * @param {string} passphrase - The passphrase to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validatePassphraseStrength = (passphrase) => {
  const errors = []

  if (!passphrase) {
    errors.push('Passphrase is required')
    return { isValid: false, errors }
  }

  if (passphrase.length < 8) {
    errors.push('Passphrase must be at least 8 characters long')
  }

  if (!/[A-Z]/.test(passphrase)) {
    errors.push('Passphrase must contain at least one uppercase letter')
  }

  if (!/[a-z]/.test(passphrase)) {
    errors.push('Passphrase must contain at least one lowercase letter')
  }

  if (!/[0-9]/.test(passphrase)) {
    errors.push('Passphrase must contain at least one number')
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(passphrase)) {
    errors.push('Passphrase must contain at least one special character')
  }

  // Check for common weak patterns
  const commonPatterns = [
    'password',
    '12345678',
    'qwerty',
    'abc123',
    'admin',
    'letmein',
    'welcome',
    'monkey',
    '123456789',
    'password123'
  ]

  if (commonPatterns.some((pattern) => passphrase.toLowerCase().includes(pattern))) {
    errors.push('Passphrase contains common weak patterns')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}
